import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import type { StreamStateValue } from "../types/streamState";
import { hasStoppedMessage, type StreamPreviewUiState, warmImageObjectUrl } from "../utils/streamPreview";

const DEFAULT_REFRESH_INTERVAL_MS = 2000;
const FAST_REFRESH_INTERVAL_MS = 1000;
const HIDDEN_REFRESH_INTERVAL_MS = 10000;
const PREVIEW_FETCH_CONCURRENCY = 8;
const PREVIEW_STEADY_SLOT_MIN_MS = 100;
const PREVIEW_RETRY_BASE_MS = 1000;
const PREVIEW_RETRY_MAX_MS = 15000;
const PREVIEW_ERROR_DISPLAY_FAILURE_THRESHOLD = 3;

type UseStreamPreviewCollectionOptions = {
  streamIds: string[];
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  getStreamStateMessage: (streamId: string) => unknown;
};

type StreamPreviewCollectionResult = {
  previewUrls: Record<string, string>;
  previewStates: Record<string, StreamPreviewUiState>;
};

const removeMissingKeys = <T extends Record<string, unknown>>(source: T, activeIds: Set<string>): T => {
  let changed = false;
  const next = { ...source };
  Object.keys(next).forEach((id) => {
    if (!activeIds.has(id)) {
      delete next[id];
      changed = true;
    }
  });
  return changed ? next : source;
};

export const useStreamPreviewCollection = ({
  streamIds,
  getStreamState,
  getStreamStateMessage
}: UseStreamPreviewCollectionOptions): StreamPreviewCollectionResult => {
  const { authorizedFetch, resolveApiPath } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [previewStates, setPreviewStates] = useState<Record<string, StreamPreviewUiState>>({});
  const previewInFlightRef = useRef<Record<string, AbortController>>({});
  const previewRetryAtRef = useRef<Record<string, number>>({});
  const previewNextAtRef = useRef<Record<string, number>>({});
  const previewFailureCountRef = useRef<Record<string, number>>({});
  const previewLastModifiedRef = useRef<Record<string, string>>({});
  const previewInitializedRef = useRef<Record<string, boolean>>({});
  const steadyCursorRef = useRef(0);
  const activeStreamIdsRef = useRef<Set<string>>(new Set());
  const isUnmountedRef = useRef(false);
  const previewUrlsRef = useRef<Record<string, string>>({});
  const [isPageHidden, setIsPageHidden] = useState(() => (typeof document !== "undefined" ? document.hidden : false));

  const orderedStreamIds = useMemo(() => Array.from(new Set(streamIds.filter((streamId) => Boolean(streamId)))), [streamIds]);
  const refreshInterval =
    orderedStreamIds.length > 0 && orderedStreamIds.length < 10
      ? FAST_REFRESH_INTERVAL_MS
      : DEFAULT_REFRESH_INTERVAL_MS;
  const effectiveRefreshInterval = isPageHidden ? Math.max(HIDDEN_REFRESH_INTERVAL_MS, refreshInterval) : refreshInterval;
  const steadySlotIntervalMs =
    orderedStreamIds.length > 0
      ? Math.max(PREVIEW_STEADY_SLOT_MIN_MS, Math.floor(effectiveRefreshInterval / orderedStreamIds.length))
      : effectiveRefreshInterval;

  const setPreviewUrlsState = (updater: (previous: Record<string, string>) => Record<string, string>) => {
    setPreviewUrls((previous) => {
      const next = updater(previous);
      previewUrlsRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    activeStreamIdsRef.current = new Set(orderedStreamIds);
  }, [orderedStreamIds]);

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsPageHidden(document.hidden);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (orderedStreamIds.length === 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setRefreshKey((value) => value + 1);
    }, steadySlotIntervalMs);
    return () => window.clearInterval(timer);
  }, [steadySlotIntervalMs, orderedStreamIds.length]);

  useEffect(() => {
    if (orderedStreamIds.length === 0) {
      return;
    }
    let cancelled = false;

    const fetchPreview = async (streamId: string) => {
      const controller = new AbortController();
      previewInFlightRef.current[streamId] = controller;
      setPreviewStates((previous) => ({ ...previous, [streamId]: "loading" }));

      try {
        const previewUrl = resolveApiPath(`/api/streams/${streamId}/preview.jpg`);
        const ifModifiedSince = previewLastModifiedRef.current[streamId];
        const response = await authorizedFetch(previewUrl, {
          signal: controller.signal,
          headers: ifModifiedSince ? { "If-Modified-Since": ifModifiedSince } : undefined
        });
        if (response.status === 304) {
          previewInitializedRef.current[streamId] = true;
          previewFailureCountRef.current[streamId] = 0;
          previewRetryAtRef.current[streamId] = 0;
          previewNextAtRef.current[streamId] = Date.now() + effectiveRefreshInterval;
          setPreviewStates((previous) => {
            if (!(streamId in previous)) {
              return previous;
            }
            const next = { ...previous };
            delete next[streamId];
            return next;
          });
          return;
        }
        if (!response.ok) {
          const error = new Error("preview unavailable") as Error & { status?: number };
          error.status = response.status;
          throw error;
        }
        const lastModified = response.headers.get("Last-Modified");
        if (lastModified) {
          previewLastModifiedRef.current[streamId] = lastModified;
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
          await warmImageObjectUrl(objectUrl);
        } catch {
          URL.revokeObjectURL(objectUrl);
          throw new Error("preview decode failed");
        }
        if (isUnmountedRef.current || !activeStreamIdsRef.current.has(streamId)) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        previewFailureCountRef.current[streamId] = 0;
        previewRetryAtRef.current[streamId] = 0;
        previewInitializedRef.current[streamId] = true;
        previewNextAtRef.current[streamId] = Date.now() + effectiveRefreshInterval;
        setPreviewStates((previous) => {
          if (!(streamId in previous)) {
            return previous;
          }
          const next = { ...previous };
          delete next[streamId];
          return next;
        });
        setPreviewUrlsState((previous) => {
          const existing = previous[streamId];
          if (existing) {
            URL.revokeObjectURL(existing);
          }
          return { ...previous, [streamId]: objectUrl };
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const failureCount = (previewFailureCountRef.current[streamId] ?? 0) + 1;
        previewFailureCountRef.current[streamId] = failureCount;
        const status =
          typeof error === "object" && error !== null && "status" in error
            ? Number((error as { status?: number }).status)
            : undefined;
        const isNotReady = status === 404;
        const shouldShowError = !isNotReady && failureCount >= PREVIEW_ERROR_DISPLAY_FAILURE_THRESHOLD;
        setPreviewStates((previous) => ({ ...previous, [streamId]: shouldShowError ? "error" : "loading" }));
        const retryDelay = Math.min(PREVIEW_RETRY_MAX_MS, PREVIEW_RETRY_BASE_MS * 2 ** Math.min(failureCount - 1, 4));
        const nextAttemptAt = Date.now() + retryDelay;
        previewRetryAtRef.current[streamId] = nextAttemptAt;
        previewNextAtRef.current[streamId] = nextAttemptAt;
      } finally {
        delete previewInFlightRef.current[streamId];
      }
    };

    const isStoppedLike = (streamId: string) => {
      const state = getStreamState(streamId);
      return state === "offline" || state === "unknown" || hasStoppedMessage(getStreamStateMessage(streamId));
    };

    const now = Date.now();
    const candidates = orderedStreamIds.filter((streamId) => {
      if (getStreamState(streamId) === "timeout" || isStoppedLike(streamId)) {
        return false;
      }
      if (previewInFlightRef.current[streamId]) {
        return false;
      }
      const retryAt = previewRetryAtRef.current[streamId] ?? 0;
      if (retryAt > now) {
        return false;
      }
      const nextAt = previewNextAtRef.current[streamId] ?? 0;
      return nextAt <= now;
    });

    if (candidates.length === 0) {
      return;
    }

    const bootstrapQueue = candidates.filter((streamId) => !previewInitializedRef.current[streamId]);
    if (bootstrapQueue.length > 0) {
      const queue = [...bootstrapQueue];
      const workerCount = Math.min(PREVIEW_FETCH_CONCURRENCY, queue.length);
      const runWorker = async () => {
        while (!cancelled) {
          const streamId = queue.shift();
          if (!streamId) {
            return;
          }
          await fetchPreview(streamId);
        }
      };
      for (let index = 0; index < workerCount; index += 1) {
        void runWorker();
      }
      return () => {
        cancelled = true;
      };
    }

    const candidateSet = new Set(candidates);
    const ordered = orderedStreamIds.filter((streamId) => candidateSet.has(streamId));
    if (ordered.length === 0) {
      return;
    }
    const startIndex = steadyCursorRef.current % ordered.length;
    let pickedId: string | null = null;
    for (let offset = 0; offset < ordered.length; offset += 1) {
      const streamId = ordered[(startIndex + offset) % ordered.length];
      if (!streamId) {
        continue;
      }
      pickedId = streamId;
      steadyCursorRef.current = (startIndex + offset + 1) % ordered.length;
      break;
    }
    if (pickedId) {
      void fetchPreview(pickedId);
    }

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, effectiveRefreshInterval, getStreamState, getStreamStateMessage, orderedStreamIds, refreshKey, resolveApiPath]);

  useEffect(() => {
    const activeStreamIds = new Set(orderedStreamIds);
    Object.keys(previewInFlightRef.current).forEach((id) => {
      if (!activeStreamIds.has(id)) {
        previewInFlightRef.current[id].abort();
        delete previewInFlightRef.current[id];
        delete previewRetryAtRef.current[id];
        delete previewNextAtRef.current[id];
        delete previewFailureCountRef.current[id];
        delete previewLastModifiedRef.current[id];
        delete previewInitializedRef.current[id];
      }
    });
    setPreviewStates((previous) => removeMissingKeys(previous, activeStreamIds));
    setPreviewUrlsState((previous) => {
      const next = removeMissingKeys(previous, activeStreamIds);
      if (next === previous) {
        return previous;
      }
      Object.keys(previous).forEach((streamId) => {
        if (!(streamId in next)) {
          URL.revokeObjectURL(previous[streamId]);
        }
      });
      return next;
    });
  }, [orderedStreamIds]);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      Object.values(previewInFlightRef.current).forEach((controller) => controller.abort());
      previewInFlightRef.current = {};
      previewRetryAtRef.current = {};
      previewNextAtRef.current = {};
      previewFailureCountRef.current = {};
      previewLastModifiedRef.current = {};
      previewInitializedRef.current = {};
      steadyCursorRef.current = 0;
      Object.values(previewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = {};
    };
  }, []);

  return { previewUrls, previewStates };
};
